import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import error from "../../utils/error";
import DatabaseAPIServices, { IDatabaseData } from "../../services/databaseApi";

type databseType = {
  isLoading: boolean,
  status: string,
  data: any,
  error?: string
}

type dbBackupDetailsType = {
  connectionId: string,
  databaseName: string,
  schemaName: string,
  tableName: string,
  selectionType: string,
  db_backup_master_id?: string,
}

interface IDatabaseState {
  databaseList: databseType,
  schemaList: databseType,
  tableList: databseType,
  s3BucketList: databseType,
  selectBackup: dbBackupDetailsType,
  saveBackupDetails: any[],
  createBackupDBDetails: {
    isSaving: boolean,
    isDeleting: boolean,
    error?: string,
    responseData? : any[]
  },
}

const defaultDatabaseValue = {
  isLoading: false,
  status: "",
  data: [],
  error: undefined
};

const initialDatabaseState: IDatabaseState = {
  databaseList: defaultDatabaseValue,
  schemaList: defaultDatabaseValue,
  tableList: defaultDatabaseValue,
  s3BucketList: defaultDatabaseValue,
  selectBackup: {
    connectionId: "",
    databaseName: "",
    schemaName: "",
    tableName: "",
    selectionType: "new"
  },
  saveBackupDetails: [],
  createBackupDBDetails: {
    isSaving: false,
    isDeleting: false,
    error: undefined,
    responseData: [],
  },
};

export const getDatabaseList = createAsyncThunk(
  "backup/getDatabaseList",
  async (payload: IDatabaseData) => {
    try {
      return DatabaseAPIServices.getDatabaseList(payload);
    } catch (error_) {
      return error_;
    }
  }
);

export const getSchemaList = createAsyncThunk(
  "backup/getSchemaList",
  async (payload: IDatabaseData) => {
    try {
      return DatabaseAPIServices.getSchemaList(payload);
    } catch (error_) {
      return error_;
    }
  }
);

export const getTableList = createAsyncThunk(
  "backup/getTableList",
  async (payload: IDatabaseData) => {
    try {
      return DatabaseAPIServices.getTableList(payload);
    } catch (error_) {
      return error_;
    }
  }
);

export const getS3BucketList = createAsyncThunk(
  "backup/getS3BucketList",
  async () => {
    try {
      return DatabaseAPIServices.getS3BucketList();
    } catch (error_) {
      return error_;
    }
  }
);

export const createBackupDBDetails = createAsyncThunk(
  "backup/createBackupDBDetails",
  async (payload: any) => {
    try {
      return DatabaseAPIServices.createBackupDBDetails(payload);
    } catch (error_) {
      return error_;
    }
  }
);

const databaseSlice = createSlice({
  name: "database",
  initialState: initialDatabaseState,
  reducers: {
    selectBackupAction: (state, action) => {
      state.selectBackup = action.payload;
    },
    saveBackupDetailsAction: (state, action) => {
      state.saveBackupDetails = [...state.saveBackupDetails, action.payload];
    },
    resetBackupDetailsAction: () => {
      console.log('HHHHH')
      initialDatabaseState.saveBackupDetails = [];
    },
  },
  extraReducers(builder): void {
    // Database list
    builder.addCase(getDatabaseList.pending, (state) => {
      state.databaseList.isLoading = true;
    });
    builder.addCase(getDatabaseList.fulfilled, (state, action) => {
      state.databaseList.isLoading = false;
      state.databaseList.data = action.payload;
    });
    builder.addCase(getDatabaseList.rejected, (state, action) => {
      state.databaseList.isLoading = false;
      state.databaseList.error = error(action.payload);
    });

    // Database schema list
    builder.addCase(getSchemaList.pending, (state) => {
      state.schemaList.isLoading = true;
    });
    builder.addCase(getSchemaList.fulfilled, (state, action) => {
      state.schemaList.isLoading = false;
      state.schemaList.data = action.payload;
    });
    builder.addCase(getSchemaList.rejected, (state, action) => {
      state.schemaList.isLoading = false;
      state.schemaList.error = error(action.payload);
    });

    // Database table list
    builder.addCase(getTableList.pending, (state) => {
      state.tableList.isLoading = true;
    });
    builder.addCase(getTableList.fulfilled, (state, action) => {
      state.tableList.isLoading = false;
      state.tableList.data = action.payload;
    });
    builder.addCase(getTableList.rejected, (state, action) => {
      state.tableList.isLoading = false;
      state.tableList.error = error(action.payload);
    });

    // S3 bucket list
    builder.addCase(getS3BucketList.pending, (state) => {
      state.s3BucketList.isLoading = true;
    });
    builder.addCase(getS3BucketList.fulfilled, (state, action) => {
      state.s3BucketList.isLoading = false;
      state.s3BucketList.data = action.payload;
    });
    builder.addCase(getS3BucketList.rejected, (state, action) => {
      state.s3BucketList.isLoading = false;
      state.s3BucketList.error = error(action.payload);
    });

    // Create backup db details
    builder.addCase(createBackupDBDetails.pending, (state) => {
      state.createBackupDBDetails.isSaving = true;
    });
    builder.addCase(createBackupDBDetails.fulfilled, (state, action) => {
      state.createBackupDBDetails.isSaving = false;
      state.createBackupDBDetails.responseData = action.payload;
    });
    builder.addCase(createBackupDBDetails.rejected, (state, action) => {
      state.createBackupDBDetails.isSaving = false;
      state.createBackupDBDetails.error = error(action.payload);
    });
  },
});

export default databaseSlice.reducer;
export const { selectBackupAction, saveBackupDetailsAction, resetBackupDetailsAction } = databaseSlice.actions
